import { defaultDataModels } from '@constants/initDataModels';
import { withDefaultSeatLimit } from '@helpers/seatLimit';
import {
  asAddDocData,
  FirestoreCollectionHandler
} from '@services/firebase/firestore/collectionHandler';
import { collectionsService } from '@services/firebase/firestore/functionFactory';
import { permissionsService } from '@services/permissions';
import { AccountContext, accountStore } from '@stores/bootstrap-stores/account';
import { appTenantState } from '@stores/bootstrap-stores/tenant';
import { log } from '@utils/log';
import type {
  ResourceGrant,
  ResourceType,
  Role,
  UserGroup
} from '@vincerosoft/assessfluence-types';
import {
  collectionRef,
  EntitySettings,
  EntityStudioConfig,
  Member
} from '@vincerosoft/assessfluence-types';
import { where } from 'firebase/firestore';
import { appUserStore } from 'src/stores/bootstrap-stores/user';

class AccountFactory {
  private getGroupEntityId() {
    const currentMember = appUserStore.getState().currentMember;
    const isOwner = permissionsService.userAccessLevel() === 'owner';
    const groupEntityId = (currentMember as { group_entity_id?: string })?.group_entity_id;

    if (!isOwner || !groupEntityId) {
      return;
    }

    return groupEntityId;
  }

  private getEntityId() {
    const currentMember = appUserStore.getState().currentMember;
    const entityId = (currentMember as { entity_id?: string })?.entity_id;
    const isEntity = ['admin', 'user', 'guest'].includes(permissionsService.userAccessLevel()!);

    if (!isEntity || !entityId) {
      return;
    }

    return entityId;
  }

  private getResourceGrants = (
    resourceGrants: ResourceGrant[],
    userGroups: UserGroup[] | null,
    userType: Role['user_type']
  ) => {
    const emptyState: { [key in 'forms' | 'formGroups']: ResourceGrant[] } = {
      forms: [],
      formGroups: []
    };

    const resourceIds = (resourceGrants ?? []).reduce<typeof emptyState>((acc, r) => {
      if (!r.id) return acc;

      if (r?.resource_type === 'form' && r.resource_id) acc.forms.push(r);
      if (r?.resource_type === 'form-group' && r.resource_id) acc.formGroups.push(r);

      return acc;
    }, emptyState);

    if (userType === 'admin') {
      return resourceIds;
    } else {
      if (!userGroups?.length) return emptyState;

      const ids = userGroups.reduce<typeof emptyState>(
        (acc, g) => {
          if (g.form_group_ids?.length) {
            for (const id of g.form_group_ids) {
              const idItem = resourceIds.formGroups.find((r) => r.id === id);
              if (idItem && acc.formGroups.findIndex((r) => r.id === id) === -1)
                acc.formGroups.push(idItem);
            }
          }
          if (g.form_ids?.length) {
            for (const id of g.form_ids) {
              const idItem = resourceIds.forms.find((r) => r.id === id);
              if (idItem && acc.forms.findIndex((r) => r.id === id) === -1) acc.forms.push(idItem);
            }
          }
          return acc;
        },
        { forms: [], formGroups: [] }
      );
      return ids;
    }
  };

  getResourceGrant = (resourceType: ResourceType, resourceId?: string) => {
    const key = resourceType === 'form' ? 'forms' : 'formGroups';
    const resources = accountStore.getState().resourceGrants || {
      [key]: []
    };
    return (resources[key] ?? [])?.find((r) => r.id === resourceId);
  };

  private getCollectionParentPath = (scope: 'standalone' | 'group', accountId: string) => {
    const collection =
      scope === 'group'
        ? collectionRef.groupEntities
        : scope === 'standalone'
          ? collectionRef.entities
          : null;
    const parentPath = collection ? `${collection}/${accountId}` : null;

    if (!parentPath) {
      throw new Error('A group entity id or entity id is required for this');
    }

    return parentPath;
  };

  getResourceGrantsPath = (accountId: string) => {
    const parentPath = `${collectionRef.entities}/${accountId}`;
    return new FirestoreCollectionHandler<ResourceGrant>(
      `${parentPath}/${collectionRef.resourceGrants}`
    );
  };

  getStudioConfigPath = (accountId: string) => {
    const parentPath = `${collectionRef.entities}/${accountId}`;

    return new FirestoreCollectionHandler<EntityStudioConfig>(
      `${parentPath}/${collectionRef.entityStudioConfig}`
    );
  };

  getStudioConfigBySourceId = async (sourceId: string, accountId: string) => {
    if (!sourceId) {
      log.warn('Skipping studio-email-config lookup: missing sourceId', {
        sourceId
      });
      return null;
    }

    const docs = await this.getStudioConfigPath(accountId).getDocs([
      where('resource.id', '==', sourceId)
    ]);
    return docs.length > 0 ? docs[0] : null;
  };

  getAccountSettingsPath = () => {
    const { scope, account } = accountStore.getState();
    const normalScope = scope === 'standalone' ? 'standalone' : 'group';
    const normalizedAccountId = scope === 'linked' ? account.group_entity_id : account.id;
    const parentPath = this.getCollectionParentPath(normalScope, normalizedAccountId!);

    return new FirestoreCollectionHandler<EntitySettings>(
      `${parentPath}/${collectionRef.entitySettings}`
    );
  };

  async getContext(
    member: Member,
    userType: Role['user_type'],
    userGroups: UserGroup[] | null
  ): Promise<AccountContext> {
    let state = {} as AccountContext;
    const isTenant = permissionsService.userAccessLevel() === 'tenant';
    const entityId = this.getEntityId();
    const groupId = this.getGroupEntityId();

    if (!isTenant && !entityId && !groupId) {
      throw new Error('Missing entity or group entity id');
    }

    const addNewAccountSettingsIfMissing = async (
      scope: 'standalone' | 'group',
      accountId: string
    ) => {
      const parentPath = this.getCollectionParentPath(scope, accountId);
      const path = new FirestoreCollectionHandler<EntitySettings>(
        `${parentPath}/${collectionRef.entitySettings}`
      );

      let accountSettings = await path.getDocs();
      if (!accountSettings || accountSettings.length === 0) {
        const newAccountSettings = defaultDataModels().entitySettings();

        await path.addDoc(newAccountSettings.id, asAddDocData(newAccountSettings));
        accountSettings = [newAccountSettings];
      }
      return accountSettings[0];
    };

    const tenantAccount = appTenantState.getState().tenantAccount!;

    if (isTenant) {
      // Tenant users do not have entity or group entity context
      state = {
        scope: 'tenant',
        account: tenantAccount,
        scopeQuery: where('domain', '==', 'TENANT'),
        docScope: {
          domain: 'TENANT'
        }
      };
      return state;
    }

    if (groupId) {
      const groupEntity = await collectionsService.groupEntities.getDoc(groupId);

      if (!groupEntity) {
        throw new Error(`Group entity with id ${member.group_entity_id} not found`);
      }
      const accountGroupEntity = withDefaultSeatLimit(groupEntity);
      const accountSettings = await addNewAccountSettingsIfMissing('group', groupId);

      state = {
        scope: 'group',
        account: accountGroupEntity,
        accountHolder: tenantAccount,
        accountSettings,
        scopeQuery: where('group_entity_id', '==', member.group_entity_id),
        docScope: { group_entity_id: accountGroupEntity.id, domain: 'GROUP_ENTITY' }
      };
      return state;
    }

    if (!['admin', 'user', 'guest'].includes(userType)) {
      throw new Error(`Invalid user type ${userType}`);
    }

    const entity = await collectionsService.entities.getDoc(entityId!);

    if (!entity) {
      throw new Error(`Entity with id ${entityId} not found`);
    }
    const accountEntity = withDefaultSeatLimit(entity);
    state.scopeQuery = where('entity_id', '==', accountEntity.id);
    state.account = accountEntity;
    state.docScope = { entity_id: accountEntity.id, domain: 'ENTITY' };
    const resourceGrantsPath = this.getResourceGrantsPath(entityId!);
    const resourceGrants = await resourceGrantsPath.getDocs();

    let resources: { [key in 'forms' | 'formGroups']: ResourceGrant[] } = {
      forms: [],
      formGroups: []
    };

    if (resourceGrants.length) {
      resources = this.getResourceGrants(resourceGrants, userGroups, userType);
    }

    state.resourceGrants = resources;

    if (accountEntity.domain === 'TENANT') {
      if (accountEntity?.group_entity_id) {
        throw new Error(`Standalone entity should not have a Group entity ID`);
      }

      const accountSettings = await addNewAccountSettingsIfMissing('standalone', entityId!);
      state.accountSettings = accountSettings;

      state.scope = 'standalone';
      state.accountHolder = tenantAccount;
    } else {
      if (!accountEntity?.group_entity_id) {
        throw new Error(`Associated entity must have a Group entity ID`);
      }
      const groupEntity = await collectionsService.groupEntities.getDoc(
        accountEntity.group_entity_id
      );

      if (!groupEntity) {
        throw new Error(`Group entity with id ${accountEntity.group_entity_id} not found`);
      }

      const accountSettings = await addNewAccountSettingsIfMissing('group', groupEntity.id);
      state.accountSettings = accountSettings;

      const accountGroupEntity = withDefaultSeatLimit(groupEntity);
      state.accountHolder = accountGroupEntity;
      state.scope = 'linked';
    }
    return state;
  }
}

export const accountService = new AccountFactory();
